import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/useToast';
import Toast from '../Toast';
import { ProductDetail, ProductItem } from '../../types/api';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { analytics, getGaClientId, getGaSessionId } from '../../utils/analytics';
import { LegalSection } from '../legal/LegalSection';

interface OrderFormProps {
  selectedProductItem: number | null;
  productDetail: ProductDetail;
  productItems: ProductItem[];
}

const OrderForm: React.FC<OrderFormProps> = ({ selectedProductItem, productDetail, productItems }) => {
  const [formData, setFormData] = useState<Record<string, string>>({
    phoneNumber: '670',
    name: '',
  });

  const { user } = useAuth();
  const isAuthenticated = !!user;
  const { t } = useTranslation('checkout');
  const [loading, setLoading] = useState(false);
  const [savedPhones, setSavedPhones] = useState<{ phone: string; label: string }[]>([]);
  const [phoneDropdownOpen, setPhoneDropdownOpen] = useState(false);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const phoneDropdownRef = useRef<HTMLDivElement>(null);
  const { toasts, showToast, removeToast} = useToast();

  // TNC state — only enforced for authenticated users (server-side flag).
  // Guests proceed without per-account TNC tracking.
  const [tncAccepted, setTncAccepted] = useState(false);
  const [showTncModal, setShowTncModal] = useState(false);
  const [acceptingTnc, setAcceptingTnc] = useState(false);
  // tncFromPayFlow distinguishes: did the customer open the TNC modal
  // because they clicked Pay (need to chain → confirm modal after Agree)
  // vs clicking the "read TNC" caption below Pay (just informational —
  // Agree only flips state and closes). Without this flag every Agree
  // would force the confirm modal, surprising customers who just wanted
  // to re-read the legal copy.
  const [tncFromPayFlow, setTncFromPayFlow] = useState(false);

  // Pre-pay confirmation: PPOB top-ups land in the game account from
  // the user-typed User ID / Server ID — a typo charges the wrong
  // account with no easy fix. Force a readback modal with a 10-second
  // countdown so the buyer actually pauses to verify.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCountdown, setConfirmCountdown] = useState(10);

  useEffect(() => {
    if (!confirmOpen) return;
    setConfirmCountdown(10);
    const id = setInterval(() => {
      setConfirmCountdown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [confirmOpen]);

  useEffect(() => {
    if (!isAuthenticated) return;
    apiService.getProfile()
      .then((res) => setTncAccepted(!!res.data?.tnc_voucher_game_accepted))
      .catch(() => {});
  }, [isAuthenticated]);

  const handleAcceptTnc = async () => {
    const fromPay = tncFromPayFlow;
    // Reset the origin flag immediately so a later caption-click open
    // doesn't accidentally inherit the previous chain.
    setTncFromPayFlow(false);

    const finish = () => {
      setTncAccepted(true);
      setShowTncModal(false);
      // Auto-chain into the pre-pay confirmation modal when the buyer
      // came from the Pay button. Caption-click readers don't get the
      // surprise of a payment confirmation popping up.
      if (fromPay) setConfirmOpen(true);
    };

    // Guest: just flip the local state + close. acceptTncVoucherGame
    // is an authenticated endpoint that persists tnc_voucher_game_accepted
    // on the user row — meaningless without a user_id, so skip the call.
    if (!isAuthenticated) {
      finish();
      return;
    }
    setAcceptingTnc(true);
    try {
      await apiService.acceptTncVoucherGame();
      finish();
    } catch {
      showToast(t('tnc_save_failed'), 'error');
    } finally {
      setAcceptingTnc(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        phoneDropdownRef.current &&
        !phoneDropdownRef.current.contains(e.target as Node) &&
        phoneInputRef.current &&
        !phoneInputRef.current.contains(e.target as Node)
      ) {
        setPhoneDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch addresses for logged-in users — prefill default phone
  useEffect(() => {
    if (!isAuthenticated) return;

    apiService.getAddresses().then((res) => {
      const addresses = res.data || [];
      const seen = new Set<string>();
      const unique = addresses.filter((a: any) => {
        if (!a.phone || seen.has(a.phone)) return false;
        seen.add(a.phone);
        return true;
      });
      setSavedPhones(unique.map((a: any) => ({ phone: a.phone, label: a.label })));
      const defaultAddr = addresses.find((a: any) => a.is_default) || addresses[0];
      if (defaultAddr?.phone) {
        setFormData(prev => ({ ...prev, phoneNumber: defaultAddr.phone }));
      }
    }).catch(() => {});
  }, [isAuthenticated]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckout = async () => {
    if (!selectedProductItem) {
      showToast(t('select_product'), 'error');
      return;
    }

    // Validate fields
    const productFieldsFilled = productDetail.userInput?.fields.every(
      (field) => formData[field.attrs.name]
    );

    if (!productFieldsFilled) {
      showToast(t('fill_fields'), 'error');
      return;
    }
    if (!formData.phoneNumber) {
      showToast(t('phone_required'), 'error');
      return;
    }
    if (!isAuthenticated && !formData.name) {
      showToast(t('name_required'), 'error');
      return;
    }

    if (isAuthenticated && !tncAccepted) {
      // Mark this open as part of the Pay flow so Agree auto-chains
      // into the confirmation modal instead of dumping the buyer back
      // at a closed-modal form.
      setTncFromPayFlow(true);
      setShowTncModal(true);
      return;
    }

    // Validation passed — open the pre-pay confirmation modal. The
    // actual order-create call runs from submitOrder() once the buyer
    // clicks Confirm after the 10s countdown.
    setConfirmOpen(true);
  };

  const submitOrder = async () => {
    if (!selectedProductItem) return;
    setConfirmOpen(false);
    setLoading(true);

    try {
      const selectedItem = productItems.find(item => item.id === selectedProductItem);
      if (!selectedItem) {
        showToast(t('select_not_found'), 'error');
        return;
      }

      // Prepare user data (exclude phone_number and name from product fields)
      const userData: Record<string, any> = {};
      productDetail.userInput?.fields.forEach((field) => {
        userData[field.attrs.name] = formData[field.attrs.name];
      });

      analytics.beginCheckout(productDetail.title, selectedItem.name, selectedItem.price);

      // Capture GA4 client_id so the backend's server-side purchase event
      // (fired on Mosan paid transition) ties back to this browsing session.
      // 100ms safety timeout — returns "" if gtag never loaded; backend
      // falls back to a synthetic id so revenue still lands.
      // session_id too — the backend purchase event needs both client_id and
      // session_id to stitch into this browsing session (funnel attribution).
      const [gaClientId, gaSessionId] = await Promise.all([
        getGaClientId(),
        getGaSessionId(),
      ]);

      // Create order
      const orderRequest = {
        product_id: productDetail.id,
        product_name: productDetail.title,
        product_item_id: selectedProductItem,
        product_item_name: selectedItem.name,
        price: selectedItem.price,
        user_data: userData,
        phone_number: formData.phoneNumber,
        name: isAuthenticated ? (user?.name || user?.email) : formData.name,
        ga_client_id: gaClientId,
        ga_session_id: gaSessionId,
      };

      const response = await apiService.createOrder(orderRequest);
      // `add_payment_info` is the intent-to-pay signal. The canonical
      // `purchase` event fires server-side once Mosan confirms payment —
      // see backend SendPPOBPurchaseEvent. Client-side `purchase` was
      // removed because Mosan WebView checkout redirects the buyer away
      // before any tab-side analytics could fire reliably.
      analytics.addPaymentInfo(response.reference, productDetail.title, selectedItem.name, selectedItem.price);

      // Mosan WebView session: backend returns a partner-redirect URL the
      // Mosan app intercepts via path marker. We MUST navigate the WebView
      // there directly so Mosan can run the native PIN/OTP flow. Skipping
      // this and going to /payment/{ref} would load Mosan's payment page
      // inside the WebView, which double-wraps the same Mosan UI.
      if (response.mosan_redirect_url) {
        window.location.href = response.mosan_redirect_url;
        return;
      }

      // Mark that we came from checkout so payment page auto-generates URL
      sessionStorage.setItem('fromCheckout', response.reference);

      // Navigate to payment page
      window.location.href = `/payment/${response.reference}`;
    } catch (error) {
      console.error('Failed to create order:', error);
      showToast(error instanceof Error ? error.message : t('create_order_failed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const selectedItem = productItems.find(item => item.id === selectedProductItem);

  return (
    <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
      {/* Title */}
      <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-6">
        {t('order_info')}
      </h2>

      {/* Form */}
      <div className="space-y-4">
        {/* Phone Number */}
        <div className="relative">
          <label
            htmlFor="phoneNumber"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {t('phone_label')}
          </label>
          <div className="relative">
            <input
              ref={phoneInputRef}
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber || ''}
              onChange={(e) => {
                handleInputChange(e);
                if (savedPhones.length > 0) setPhoneDropdownOpen(true);
              }}
              onFocus={() => {
                if (savedPhones.length > 0) setPhoneDropdownOpen(true);
              }}
              placeholder="08123456789"
              autoComplete="off"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            {savedPhones.length > 0 && (
              <button
                type="button"
                onClick={() => setPhoneDropdownOpen(!phoneDropdownOpen)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className={`w-4 h-4 transition-transform ${phoneDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}
          </div>
          {phoneDropdownOpen && savedPhones.length > 0 && (
            <div
              ref={phoneDropdownRef}
              className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
            >
              {savedPhones
                .filter(sp => sp.phone.includes(formData.phoneNumber || ''))
                .map((sp, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, phoneNumber: sp.phone }));
                      setPhoneDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                      formData.phoneNumber === sp.phone ? 'bg-primary/5' : ''
                    }`}
                  >
                    <span className="text-sm font-medium text-gray-900">{sp.phone}</span>
                    <span className="ml-2 text-xs text-gray-500">({sp.label})</span>
                  </button>
                ))}
              {savedPhones.filter(sp => sp.phone.includes(formData.phoneNumber || '')).length === 0 && (
                <div className="px-4 py-2.5 text-sm text-gray-400">{t('no_matching_phones')}</div>
              )}
            </div>
          )}
        </div>

        {/* Name - only for guest */}
        {!isAuthenticated && (
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t('name_label')}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name || ''}
              onChange={handleInputChange}
              placeholder={t('name_placeholder')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        )}

        {/* Dynamic Fields from Product Detail */}
        {productDetail.userInput?.fields.map((field) => {
          const fieldName = field.attrs.name as string;
          const i18n = productDetail.i18n?.userInputFieldsI18n?.[fieldName];
          const label = i18n?.placeholder_en || field.attrs.placeholder as string;
          return (
            <div key={fieldName}>
              <label
                htmlFor={fieldName}
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {label} *
              </label>
              <input
                type={field.attrs.type as string}
                id={fieldName}
                name={fieldName}
                value={formData[fieldName] || ''}
                onChange={handleInputChange}
                placeholder={label}
                maxLength={field.attrs.length as number}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          );
        })}
      </div>

      {/* Divider */}
      <div className="my-6 border-t border-gray-200"></div>

      {/* Payment Method — only Mosan is wired today, but the row is
          rendered as a selectable card for parity with the e-commerce
          checkout (CheckoutOrderSummary's Payment Method block).
          Locked-selected since it's the sole option. */}
      <div className="mb-4">
        <h3 className="text-sm sm:text-[16px] font-medium text-gray-800 mb-2 sm:mb-3">
          {t('payment_method')}
        </h3>
        <div className="w-full flex items-center px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg">
          <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-primary flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-primary" />
          </div>
          <span className="ml-2 text-xs sm:text-[16px] text-gray-800">{t('mosan_wallet')}</span>
        </div>
      </div>

      {/* Selected Product Info */}
      {selectedItem && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="mb-3">
            <span className="text-xs text-gray-500 uppercase tracking-wide">{t('selected_product')}</span>
            <p className="text-base font-semibold text-gray-800 mt-1">{selectedItem.name}</p>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
            <span className="text-sm text-gray-600">{t('total_price')}</span>
            <span className="text-xl font-bold text-primary">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
              }).format(selectedItem.price)}
            </span>
          </div>
        </div>
      )}

      {/* Checkout Button */}
      <button
        onClick={handleCheckout}
        disabled={!selectedProductItem || loading}
        className={`w-full py-4 font-bold rounded-lg transition-all ${
          selectedProductItem && !loading
            ? 'bg-primary text-white hover:brightness-90 shadow-lg hover:shadow-xl'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
        }`}
      >
        {loading ? t('processing') : t('pay_button')}
      </button>

      {/* TNC caption — shown to everyone (guest checkout supported on
          PPOB). Auth gate dropped because guests can buy too and still
          need to see the legal contract before paying. */}
      <p className="mt-2 text-xs text-center text-gray-500">
        {t('tnc_prefix')}{' '}
        <button
          type="button"
          onClick={() => setShowTncModal(true)}
          className="text-primary hover:underline font-medium"
        >
          {t('tnc_link')}
        </button>
        .
      </p>

      {/* Helper Text */}
      {!selectedProductItem && (
        <p className="mt-3 text-xs text-center text-gray-500">
          {t('select_to_continue')}
        </p>
      )}

      {/* Pre-pay Confirmation Modal — readback of every user-typed
          field with a 10-second cooldown on the Confirm button so the
          buyer doesn't reflex-tap past a typo. */}
      {confirmOpen && (() => {
        const selectedItem = productItems.find((i) => i.id === selectedProductItem);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="bg-white rounded-lg max-w-md w-full flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-800">{t('confirm_title')}</h2>
                <p className="text-sm text-gray-500 mt-1">{t('confirm_subtitle')}</p>
              </div>
              <div className="p-6 space-y-3 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">{productDetail.title}</span>
                  <span className="font-medium text-gray-900 text-right">{selectedItem?.name}</span>
                </div>
                {productDetail.userInput?.fields.map((f) => (
                  <div key={f.attrs.name} className="flex justify-between gap-3">
                    <span className="text-gray-500">{f.attrs.placeholder || f.attrs.name}</span>
                    <span className="font-mono font-medium text-gray-900 text-right break-all">{formData[f.attrs.name]}</span>
                  </div>
                ))}
                <div className="flex justify-between gap-3">
                  {/* phone_number key lives in the 'order' i18n
                      namespace, this component is bound to 'checkout'
                      via useTranslation('checkout') — reach across
                      with the order: prefix instead of duplicating
                      the key. */}
                  <span className="text-gray-500">{t('order:phone_number', { defaultValue: 'Phone Number' })}</span>
                  {/* Strip a leading + if present — Mosan auto-fill
                      pre-pends one, otherwise the user types a raw
                      country code. Either way we render exactly one +
                      in the readback. */}
                  <span className="font-mono font-medium text-gray-900 text-right">+{(formData.phoneNumber || '').replace(/^\+/, '')}</span>
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmOpen(false)}
                  className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
                >
                  {t('confirm_cancel')}
                </button>
                <button
                  type="button"
                  onClick={submitOrder}
                  disabled={confirmCountdown > 0}
                  className={`flex-1 py-2.5 rounded-lg font-medium transition-colors ${
                    confirmCountdown > 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-primary text-white hover:brightness-90'
                  }`}
                >
                  {confirmCountdown > 0
                    ? t('confirm_button_idle', { seconds: confirmCountdown })
                    : t('confirm_button_ready')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* TNC Modal */}
      {showTncModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-lg max-w-lg w-full flex flex-col" style={{ maxHeight: 'calc(100% - 2rem)' }}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">{t('tnc_link')}</h2>
            </div>
            <div className="p-6 overflow-y-auto flex-1 text-sm">
              <LegalSection slug="tnc-ppob" />
              {!tncAccepted && (
                <p className="text-gray-700 leading-relaxed border-t border-gray-200 pt-4 mt-4">
                  {t('tnc_modal_disclaimer')}
                </p>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              {tncAccepted ? (
                <button
                  onClick={() => setShowTncModal(false)}
                  className="flex-1 py-2.5 px-4 bg-primary text-white rounded-lg hover:brightness-90 transition-colors font-medium"
                >
                  {t('tnc_close')}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setShowTncModal(false)}
                    className="flex-1 py-2.5 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    {t('tnc_cancel')}
                  </button>
                  <button
                    onClick={handleAcceptTnc}
                    disabled={acceptingTnc}
                    className="flex-1 py-2.5 px-4 bg-primary text-white rounded-lg hover:brightness-90 transition-colors font-medium disabled:bg-gray-400"
                  >
                    {acceptingTnc ? t('tnc_processing') : t('tnc_agree')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

export default OrderForm;
